import { CookieEnum, createCookie } from "@/shared/utils/cookie";
import { cookieOptions } from "./index";
import { User } from "../hooks/useAuth";

/**
 * Ensures user ID starts with "CustomerUser_" prefix
 * @param id - The user ID to check and potentially prefix
 * @returns The ID with CustomerUser_ prefix
 */
export const ensureCustomerUserPrefix = (
  id: string | null | undefined
): string | null => {
  if (!id) return id || null;
  return id.startsWith("CustomerUser_") ? id : `CustomerUser_${id}`;
};

interface LoginResponseData {
  _id: string;
  name: string;
  email: string;
  phone: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
}

interface LoginUpdateParams {
  loginResponseData: LoginResponseData;
  token?: string | null;
  updateUser: (userData: User) => void;
}

/**
 * Centralized function to handle user login updates
 * Updates user context and creates authentication cookie
 *
 * This function consolidates the repeated login update logic found across:
 * - CentralizedModal.tsx
 * - SideCart.tsx
 * - AuthProvider.tsx
 *
 * @param loginResponseData - User data from login/signup response
 * @param token - Authentication token
 * @param updateUser - Function to update user context
 */
export const handleLoginUpdate = ({
  loginResponseData,
  token,
  updateUser,
}: LoginUpdateParams): void => {
  // Update user context
  updateUser({
    id: ensureCustomerUserPrefix(loginResponseData._id),
    name: loginResponseData.name,
    email: loginResponseData.email,
    phone: loginResponseData.phone,
    isEmailVerfied: loginResponseData.emailVerified ?? false,
    isPhoneVerified: loginResponseData.phoneVerified ?? false,
    token: token,
  });

  // Create authentication cookie and store in localStorage
  if (token) {
    createCookie(CookieEnum.AUTH_TOKEN, token, cookieOptions);
    createCookie(CookieEnum.CLIENT_CENTRAL_TOKEN, token, cookieOptions);
    localStorage.setItem(CookieEnum.CLIENT_CENTRAL_TOKEN, token);
  }
};
